import { useState, useEffect } from "react";
import { cn } from "@/lib/utils";
import { useImpersonation } from "@/contexts/ImpersonationContext";
import { hasMinRole } from "@/lib/constants";
import { useChatRooms, useChatUnreadTotal } from "@/hooks/use-chat-rooms";
import type { ChatRoom } from "@/hooks/use-chat-rooms";
import { ChatRoomList } from "./ChatRoomList";
import { ChatWindow } from "./ChatWindow";
import { NewChatDialog } from "./NewChatDialog";
import { SrIcon } from "@/components/ui/SrIcon";

/**
 * Messenger-style floating chat: FAB nederst til høyre som åpner et panel
 * med romliste + chat-vindu. VIB-427 PoC.
 */
export function ChatLauncher() {
  // Chat er privat og RLS autoriserer alltid den faktisk innloggede brukeren.
  // Bruk derfor selfUser også under impersonering; ellers forsøker klienten å
  // opprette/lese rom som impersonert bruker mens JWT-en tilhører adminen.
  const { selfUser: user } = useImpersonation();
  const userId = user?.id ?? null;
  const [open, setOpen] = useState(false);
  const [activeRoomId, setActiveRoomId] = useState<string | null>(null);
  const [newChatOpen, setNewChatOpen] = useState(false);

  const { data: rooms, isLoading } = useChatRooms(userId);
  const unreadTotal = useChatUnreadTotal(userId);

  const activeRoom: ChatRoom | null = activeRoomId
    ? rooms?.find((r) => r.id === activeRoomId) ?? null
    : null;

  // Lukk aktivt rom hvis det forsvinner fra listen
  useEffect(() => {
    if (activeRoomId && rooms && !rooms.some((r) => r.id === activeRoomId)) {
      setActiveRoomId(null);
    }
  }, [rooms, activeRoomId]);

  // VIB-1055 / VIB-2712: roller under ansatt-nivå skal ikke ha chat. Testen
  // spør om NIVÅ, ikke navn: sto det `role === "external"` her, ville neste
  // rolle (som `skjerm` på en TV i resepsjonen) fått chat-knappen gratis.
  // Samme regel som `user_lacks_chat_access` i databasen.
  if (!userId || !hasMinRole(user?.role ?? "", "user")) return null;

  return (
    <>
      {/* Panel */}
      {open && (
        <div
          className={cn(
            "fixed z-40 bg-background border rounded-xl shadow-2xl overflow-hidden flex flex-col",
            "bottom-20 right-6 w-[360px] h-[520px]",
            "max-sm:bottom-16 max-sm:right-3 max-sm:left-3 max-sm:w-auto max-sm:h-[70vh]",
          )}
        >
          {activeRoom ? (
            <ChatWindow
              room={activeRoom}
              currentUserId={userId}
              currentUserName={user?.full_name ?? null}
              onBack={() => setActiveRoomId(null)}
            />
          ) : (
            <ChatRoomList
              rooms={rooms ?? []}
              isLoading={isLoading}
              currentUserId={userId}
              onSelect={(room) => setActiveRoomId(room.id)}
              onNewChat={() => setNewChatOpen(true)}
            />
          )}
        </div>
      )}

      {/* Pill-knapp — VIB-727: rektangulær pill i stedet for rund FAB,
          så den tar mindre høyde og kolliderer mindre med bunn-innhold. */}
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-label={open ? "Lukk chat" : "Åpne chat"}
        className={cn(
          "fixed bottom-6 right-6 z-40 h-10 px-4 rounded-full shadow-lg",
          "bg-primary text-primary-foreground hover:brightness-110 transition-all",
          "flex items-center gap-2 text-sm font-medium",
          "max-sm:bottom-4 max-sm:right-4",
        )}
      >
        {open ? (
          <>
            <SrIcon name="x" className="w-4 h-4" />
            <span>Lukk</span>
          </>
        ) : (
          <>
            <SrIcon name="message-square" className="w-4 h-4" />
            <span>Chat</span>
          </>
        )}
        {!open && unreadTotal > 0 && (
          <span className="ml-1 min-w-[20px] h-[20px] rounded-full bg-destructive text-destructive-foreground text-[11px] font-semibold inline-flex items-center justify-center px-1.5">
            {unreadTotal > 99 ? "99+" : unreadTotal}
          </span>
        )}
      </button>

      <NewChatDialog
        open={newChatOpen}
        onOpenChange={setNewChatOpen}
        currentUserId={userId}
        onCreated={(roomId) => setActiveRoomId(roomId)}
      />
    </>
  );
}

export default ChatLauncher;
